import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { readPage, readItem } from '../../../utils/apiResult';
import { IDP_STATUSES } from '../../my-development-plans/config';

// Task 23/35 — eski Faz A tek-onaylayıcı akışı (approve/request-revision uçları)
// KALKTI; yerine görüşme aşamalı yeni akış geldi: record-meeting → decide (approve|
// revision) → start-period-end-review → complete-review. Eski uçlara giden istekler
// artık 404 döner (sessiz-kırık) — bu dosya yeni sözleşmeye göre yeniden yazıldı.
const useDevelopmentPlanApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  // TD-23 — "yüklenemedi" ≠ "boş". Sinyal res.data.status'tur; interceptor GET'lerde
  // reject ETMEDİĞİ için .catch() hiç çalışmaz (bkz. utils/apiResult.js).
  const [loadFailed, setLoadFailed] = useState(false);

  const [fetchingPending, setFetchingPending] = useState(true);
  const [pendingData, setPendingData] = useState([]);
  const [pendingLoadFailed, setPendingLoadFailed] = useState(false);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : { pageNumber: 1, pageSize: 10 };
    setFetching(true);
    api.get(`/v1/development-plans?${new URLSearchParams(params)}`)
      .then((res) => {
        const { ok, items, pagination: page } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
        setPagination(page);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  useEffect(() => {
    setFetchingPending(true);
    api.get('/v1/development-plans/pending')
      .then((res) => {
        const { ok, items } = readPage(res);
        setPendingLoadFailed(!ok);
        setPendingData(items);
      })
      .finally(() => setFetchingPending(false));
  }, [refreshTrigger]);

  // TD-23 — başarısız okumada undefined DEĞİL açıkça null döner; çağıran "detay yok"
  // ile "detay yüklenemedi"yi ayırt edebilsin diye tek doğru sinyal res.data.status.
  const getById = useCallback(async (id) => {
    const res = await api.get(`/v1/development-plans/${id}`);
    return readItem(res);
  }, []);

  // Yönetici — meeting -> manager_approval (ekip-kapsamı kontrolü servis içinde).
  const recordMeeting = useCallback(async (id, payload) => {
    try {
      await api.post(`/v1/development-plans/${id}/record-meeting`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // Yönetici — approve|revision (ekip-kapsamı kontrolü servis içinde).
  const decide = useCallback(async (id, payload) => {
    try {
      await api.post(`/v1/development-plans/${id}/decide`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // active -> period_end_review (dönem bağlantısı flag'i kapalıysa manuel tetik).
  const startPeriodEndReview = useCallback(async (id) => {
    try {
      await api.post(`/v1/development-plans/${id}/start-period-end-review`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // period_end_review -> completed; çalışan+yönetici değerlendirme metinleri (ikisi de zorunlu).
  const completeReview = useCallback(async (id, payload) => {
    try {
      await api.post(`/v1/development-plans/${id}/complete-review`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // İK manuel IDP daveti — dönem bağlı DEĞİLSE kullanılır (yalnız bildirim, plan oluşturmaz).
  const invite = useCallback(async (employeeIds) => {
    try {
      await api.post('/v1/development-plans/invite', { employeeIds });
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'managerNote', label: t('performance.idp.managerNotes'), options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'description', label: t('performance.idp.description'), options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'overallProgress', label: t('performance.idp.overallProgress'), options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'readyForReview', label: ' ', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'goals', label: ' ', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeName',
      label: t('performance.idp.employee'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'title',
      label: t('performance.idp.titleField'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'cycleName',
      label: t('performance.idp.cycle'),
      options: { filter: false, sort: false },
    },
    {
      name: 'status',
      label: t('performance.idp.fields.status'),
      options: {
        filter: true, sort: false, filterType: 'custom',
        customBodyRender: (v) => (v ? t(`performance.idp.status.${v}`, { defaultValue: v }) : '-'),
        filterOptions: {
          // Backend `List` yalnız `status[in]` okur (StatusIn, CSV) — bkz. my-development-plans/config.
          defaultOp: 'in',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('performance.idp.fields.status')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => {
                  fl[i] = e.target.value ? [e.target.value] : [];
                  onChange(fl[i], i, col);
                }}
              >
                <MenuItem value=""><em>-</em></MenuItem>
                {IDP_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{t(`performance.idp.status.${s}`, { defaultValue: s })}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    dateFilterColumn('createDate', t('performance.common.createDate'), lang),
  ], [t, lang]);

  const pendingColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'description', label: t('performance.idp.description'), options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'overallProgress', label: t('performance.idp.overallProgress'), options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'goals', label: ' ', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeName',
      label: t('performance.idp.employee'),
      options: { filter: false, sort: true },
    },
    {
      name: 'title',
      label: t('performance.idp.titleField'),
      options: { filter: false, sort: true },
    },
    {
      name: 'cycleName',
      label: t('performance.idp.cycle'),
      options: { filter: false, sort: false },
    },
    dateFilterColumn('createDate', t('performance.common.createDate'), lang, { clientSide: true }),
  ], [t, lang]);

  return {
    fetching,
    data,
    pagination,
    loadFailed,
    columns,
    pendingColumns,
    fetchingPending,
    pendingData,
    pendingLoadFailed,
    getById,
    recordMeeting,
    decide,
    startPeriodEndReview,
    completeReview,
    invite,
    refresh,
  };
};

export default useDevelopmentPlanApi;
